import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Typography, Slide } from '@material-ui/core';

import { PASSWORD_MAX_LENGTH } from 'settings/constants/common';

import { FIELD_TYPE_PASSWORD, FIELD_TYPE_TEXT } from 'settings/constants/transaction';

import Icon from '../common/Icon';
import Textfield from '../common/Textfield';

import { Avatar } from '../common/avatars';
import { BasicButton, IconButton } from '../common/buttons';

const FLEX_SPACING = 2;
const LOGIN_STEP_INITIAL = 'INITIAL';
const LOGIN_STEP_FINAL = 'FINAL';

// eslint-disable-next-line react/display-name
const Transition = React.forwardRef((props, ref) => <Slide direction="down" ref={ref} {...props} />);

// Login page modal. Pre-defined styling for every element. Only contents are changing
const LoginModal = ({
  id,
  isOpen,
  cover,
  avatar,
  className,
  username,
  password,
  currentStep,
  onClose,
  onLogin,
  onChange,
  onForgotLogin,
  onPhraseChecking,
}) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const primaryButtonTitle =
    currentStep === LOGIN_STEP_INITIAL ? formatMessage({ id: 'buttonText.yes' }) : formatMessage({ id: 'buttonText.login' });

  const secondaryButtonTitle =
    currentStep === LOGIN_STEP_INITIAL ? formatMessage({ id: 'buttonText.notMe' }) : formatMessage({ id: 'buttonText.forgotLogin' });

  // Disable user from click login if there is no password input (same as my2u MY)
  const disableLoginButton = currentStep === LOGIN_STEP_FINAL && !password;

  const handleSelfUnlockModal = () => {
    onClose();
    onForgotLogin(true);
  };

  const handlePrimaryBtnClick = () => (currentStep === LOGIN_STEP_INITIAL ? onPhraseChecking() : onLogin());
  const handleSecondaryBtnClick = () => (currentStep === LOGIN_STEP_INITIAL ? onClose() : handleSelfUnlockModal());

  const [isPeekOpen, setPeek] = useState(false);

  return (
    <Dialog
      maxWidth="xs"
      open={isOpen}
      onClose={onClose}
      aria-labelledby={id}
      disableEscapeKeyDown
      fullScreen={fullScreen}
      TransitionComponent={Transition}
      className={`home home--login-modal ${className}`}
    >
      <DialogTitle disableTypography id={id} style={{ backgroundImage: `url('${cover}')` }}>
        <div className="flex justify-end">
          <IconButton onClick={onClose} className="close-btn">
            <Icon isAssetIcon={false} type="close" />
          </IconButton>
        </div>
        <Avatar alt="profile avatar" src={avatar} className="avatar--primary profile-image" />
      </DialogTitle>

      <DialogContent>
        <Grid container direction="column" alignItems="center">
          <Typography variant="h4" className="tc username">{`${formatMessage({ id: 'login.greeting' })}, ${username}!`}</Typography>
          {currentStep === LOGIN_STEP_INITIAL ? (
            <div className="w-100 mt4 mb2 tl">
              <Typography className="tc captcha--label">{formatMessage({ id: 'login.captcha.label' })}</Typography>
            </div>
          ) : (
            <div className="mt3 mb3 w-100">
              <Textfield
                id="login-password"
                fullWidth
                isLeftAsset
                hasLeftIcon
                isRightAsset
                hasRightIcon
                value={password}
                maxLength={PASSWORD_MAX_LENGTH}
                type={isPeekOpen ? FIELD_TYPE_TEXT : FIELD_TYPE_PASSWORD}
                rightIcon={isPeekOpen ? 'eyeClose' : 'eyeOpen'}
                leftIcon="userRound"
                className="mt3 textfield--login"
                onIconClick={() => setPeek(!isPeekOpen)}
                onChange={e => onChange('password', e.target.value)}
                placeholder={formatMessage({ id: 'login.password.placeholder' })}
              />
            </div>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Grid container spacing={FLEX_SPACING} justify="center">
          <Grid item xs={12} sm={6}>
            <BasicButton title={secondaryButtonTitle} className="transparent-bb db modal-action" onClick={handleSecondaryBtnClick} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <BasicButton
              title={primaryButtonTitle}
              className="db modal-action"
              onClick={handlePrimaryBtnClick}
              disabled={disableLoginButton}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

LoginModal.defaultProps = { id: '', className: '' };

LoginModal.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  username: PropTypes.string.isRequired,
  password: PropTypes.string.isRequired,
  avatar: PropTypes.string.isRequired,
  currentStep: PropTypes.string.isRequired,
  cover: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onForgotLogin: PropTypes.func.isRequired,
  onPhraseChecking: PropTypes.func.isRequired,
};

export default LoginModal;
